import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthRevalidationFn } from '../lib/auth';
import {
  createWatchlist,
  deleteWatchlist,
  fetchWatchlists,
  renameWatchlist,
  reorderWatchlists,
  replaceWatchlistSymbols,
  shouldRevalidateWatchlistAuth,
  type UserWatchlist,
  type WatchlistCollection,
} from '../lib/watchlists';

export function useWatchlists(onAuthRevalidationRequested: AuthRevalidationFn, enabled: boolean) {
  const [collection, setCollection] = useState<WatchlistCollection | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const mountedRef = useRef(false);
  const authRef = useRef(onAuthRevalidationRequested);
  const pendingWatchlistIdRef = useRef(0);

  useEffect(() => {
    authRef.current = onAuthRevalidationRequested;
  }, [onAuthRevalidationRequested]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let cancelled = false;
    mountedRef.current = true;

    const load = async () => {
      try {
        const next = await fetchWatchlists();
        if (!cancelled && mountedRef.current) {
          setCollection(next);
          setLoadError(null);
        }
      } catch (error) {
        if (cancelled || !mountedRef.current) {
          return;
        }

        setLoadError(error instanceof Error ? error.message : 'Failed to load watchlists');
        if (shouldRevalidateWatchlistAuth(error)) {
          void authRef.current('background');
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [enabled, loadAttempt]);

  const mutate = useCallback(async <T>(operation: () => Promise<T>, apply: (result: T) => void) => {
    setIsMutating(true);
    setMutationError(null);
    try {
      const result = await operation();
      if (mountedRef.current) {
        apply(result);
      }
      return result;
    } catch (error) {
      if (mountedRef.current) {
        setMutationError(error instanceof Error ? error.message : 'Failed to update watchlists');
      }
      if (shouldRevalidateWatchlistAuth(error)) {
        void authRef.current('background');
      }
      throw error;
    } finally {
      if (mountedRef.current) {
        setIsMutating(false);
      }
    }
  }, []);

  const patchWatchlists = useCallback((patch: (current: UserWatchlist[]) => UserWatchlist[]) => {
    setCollection((current) =>
      current == null ? current : { ...current, watchlists: patch(current.watchlists) },
    );
  }, []);

  const create = useCallback(
    async (name: string, tickers: readonly string[]): Promise<UserWatchlist> => {
      pendingWatchlistIdRef.current += 1;
      const pendingId = `__pending_watchlist_${pendingWatchlistIdRef.current}`;
      const normalizedName = name.trim().replace(/\s+/g, ' ');
      const normalizedTickers = tickers.map((ticker) => ticker.trim().toUpperCase());

      setIsMutating(true);
      setMutationError(null);
      patchWatchlists((current) => [
        ...current,
        {
          id: pendingId,
          name: normalizedName,
          position: Math.max(-1, ...current.map((watchlist) => watchlist.position)) + 1,
          symbols: normalizedTickers.map((ticker, position) => ({ ticker, position })),
        },
      ]);

      try {
        const created = await createWatchlist(name, tickers);
        if (mountedRef.current) {
          patchWatchlists((current) =>
            current.map((watchlist) => (watchlist.id === pendingId ? created : watchlist)),
          );
        }
        return created;
      } catch (error) {
        if (mountedRef.current) {
          patchWatchlists((current) => current.filter((watchlist) => watchlist.id !== pendingId));
          setMutationError(error instanceof Error ? error.message : 'Failed to update watchlists');
        }
        if (shouldRevalidateWatchlistAuth(error)) {
          void authRef.current('background');
        }
        throw error;
      } finally {
        if (mountedRef.current) {
          setIsMutating(false);
        }
      }
    },
    [patchWatchlists],
  );

  return {
    collection,
    loadError,
    retryLoad: () => {
      setLoadError(null);
      setLoadAttempt((current) => current + 1);
    },
    mutationError,
    isMutating,
    create,
    rename: (id: string, name: string) =>
      mutate(
        () => renameWatchlist(id, name),
        (renamed) => {
          patchWatchlists((current) =>
            current.map((watchlist) =>
              watchlist.id === renamed.id ? { ...watchlist, name: renamed.name } : watchlist,
            ),
          );
        },
      ),
    remove: (id: string) =>
      mutate(
        () => deleteWatchlist(id),
        (deleted) => {
          patchWatchlists((current) =>
            current
              .filter((watchlist) => watchlist.id !== deleted.id)
              .map((watchlist, position) => ({ ...watchlist, position })),
          );
        },
      ),
    reorder: (ids: readonly string[]) =>
      mutate(
        () => reorderWatchlists(ids),
        (orderedIds) => {
          patchWatchlists((current) => {
            const byId = new Map(current.map((watchlist) => [watchlist.id, watchlist]));
            return orderedIds.map((id, position) => ({ ...byId.get(id)!, position }));
          });
        },
      ),
    replaceSymbols: (id: string, tickers: readonly string[]) =>
      mutate(
        () => replaceWatchlistSymbols(id, tickers),
        (updated) => {
          patchWatchlists((current) =>
            current.map((watchlist) =>
              watchlist.id === updated.id ? { ...watchlist, symbols: updated.symbols } : watchlist,
            ),
          );
        },
      ),
    clearMutationError: () => setMutationError(null),
  };
}
